import { useState } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { KpiHistoryPoint } from './ProjectDashboard';

// Convert a stored ISO timestamp ("2026-05-19T12:00:00.000Z") into a
// value the <input type="date"> control will accept (YYYY-MM-DD).
function isoToDateInput(iso: string): string {
  // Trim the time portion; tolerant of both "YYYY-MM-DDTHH:MM:SSZ" and bare "YYYY-MM-DD".
  return iso.slice(0, 10);
}

// Convert a date-input value back to an ISO timestamp at noon UTC. Noon
// avoids the dreaded "saved Apr 17, displayed Apr 16" bug from negative
// timezones when only the calendar date matters.
function dateInputToIso(date: string): string {
  return `${date}T12:00:00.000Z`;
}

function formatDateForDisplay(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[m - 1]} ${d}, ${y}`;
}

interface KpiHistoryPanelProps {
  history: KpiHistoryPoint[]; // oldest → newest
  unit: string | null;
  readOnly?: boolean;
  onAddPoint: (value: number, recordedAt: string) => Promise<void>;
  onUpdatePoint: (id: string, value: number, recordedAt: string) => Promise<void>;
  onDeletePoint: (id: string) => Promise<void>;
}

export function KpiHistoryPanel({
  history,
  unit,
  readOnly = false,
  onAddPoint,
  onUpdatePoint,
  onDeletePoint,
}: KpiHistoryPanelProps) {
  // Editing state: which row is open, and the staged value/date for it
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [editDate, setEditDate] = useState<string>('');
  const [busyId, setBusyId] = useState<string | null>(null);

  // "Add measurement" row
  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState<string>('');
  const [newDate, setNewDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [addBusy, setAddBusy] = useState(false);

  const unitSuffix = unit ? ` ${unit}` : '';
  const sorted = [...history].sort((a, b) => b.recorded_at.localeCompare(a.recorded_at)); // newest first

  const startEdit = (point: KpiHistoryPoint) => {
    setEditingId(point.id);
    setEditValue(point.value.toString());
    setEditDate(isoToDateInput(point.recorded_at));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
    setEditDate('');
  };

  const saveEdit = async (id: string) => {
    const valueNum = parseFloat(editValue);
    if (!Number.isFinite(valueNum) || !editDate) return;
    setBusyId(id);
    try {
      await onUpdatePoint(id, valueNum, dateInputToIso(editDate));
      cancelEdit();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setBusyId(id);
    try {
      await onDeletePoint(id);
    } finally {
      setBusyId(null);
    }
  };

  const handleAdd = async () => {
    const valueNum = parseFloat(newValue);
    if (!Number.isFinite(valueNum) || !newDate) return;
    setAddBusy(true);
    try {
      await onAddPoint(valueNum, dateInputToIso(newDate));
      setNewValue('');
      setNewDate(new Date().toISOString().slice(0, 10));
      setIsAdding(false);
    } finally {
      setAddBusy(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-600">
          Measurements ({history.length})
        </span>
        {!readOnly && !isAdding && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setIsAdding(true)}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add measurement
          </Button>
        )}
      </div>

      {history.length === 0 && !isAdding && (
        <p className="text-xs text-gray-400 italic py-2">
          No historical measurements yet. Each change to the current value is
          captured automatically; you can also add past measurements manually.
        </p>
      )}

      {/* New-measurement row */}
      {isAdding && (
        <div className="flex items-center gap-2 mb-2 p-2 bg-white rounded border border-ingesta-blue-200">
          <Input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            className="w-36 text-sm h-8"
          />
          <Input
            type="number"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder="value"
            className="w-24 text-sm h-8"
            autoFocus
          />
          {unit && <span className="text-xs text-gray-500">{unit}</span>}
          <div className="flex-1" />
          <Button
            size="sm"
            className="h-8"
            onClick={handleAdd}
            disabled={addBusy || !newValue || !newDate}
          >
            {addBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8"
            onClick={() => {
              setIsAdding(false);
              setNewValue('');
            }}
            disabled={addBusy}
          >
            Cancel
          </Button>
        </div>
      )}

      {/* History rows (newest first) */}
      {sorted.length > 0 && (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {sorted.map((point) => {
            const isEditing = editingId === point.id;
            const isBusy = busyId === point.id;
            return (
              <div
                key={point.id}
                className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-white rounded group"
              >
                {isEditing ? (
                  <>
                    <Input
                      type="date"
                      value={editDate}
                      onChange={(e) => setEditDate(e.target.value)}
                      className="w-36 text-sm h-8"
                    />
                    <Input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="w-24 text-sm h-8"
                    />
                    {unit && <span className="text-xs text-gray-500">{unit}</span>}
                    <div className="flex-1" />
                    <Button
                      size="sm"
                      className="h-8"
                      onClick={() => saveEdit(point.id)}
                      disabled={isBusy || !editValue || !editDate}
                    >
                      {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={cancelEdit} disabled={isBusy}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="w-28 text-gray-500 text-xs">
                      {formatDateForDisplay(point.recorded_at)}
                    </span>
                    <button
                      type="button"
                      disabled={readOnly}
                      onClick={() => !readOnly && startEdit(point)}
                      className={`flex-1 text-left font-medium tabular-nums ${
                        readOnly ? 'cursor-default' : 'cursor-pointer hover:text-ingesta-blue-700'
                      }`}
                    >
                      {point.value}
                      {unitSuffix}
                    </button>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => handleDelete(point.id)}
                        disabled={isBusy}
                        className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                        title="Delete measurement"
                      >
                        {isBusy ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3" />
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
